import { businessToday, daysFromNow, formatBusinessDate } from './DateUtils';
import { groupBy } from './ObjectUtils';

export const aggregateBalances = <T extends { balance: number }>(
  walletBalances: T[],
  getGroupKey: (walletBalance: T) => string,
): T[] => {
  const grouped = groupBy(walletBalances, getGroupKey);
  return Object.values(grouped)
    .map(group => ({ ...group[0], balance: group.reduce((sum, w) => sum + w.balance, 0) }))
    .filter(wallet => wallet.balance !== 0);
};

export const isBalanceLocked = <T extends {
  lockedUntilDate?: string | null,
}> (balance: T): boolean => {
  return !!balance.lockedUntilDate && balance.lockedUntilDate > businessToday();
};

/**
 * Every unexpired, unreserved point, locked or not. This is the wallet's total holding - use it to
 * decide whether there is anything to show, not to tell someone what they can spend.
 */
export const calculateUnexpiredPoints = <T extends {
  bookingId: string | null,
  expirationDate: string | null,
  points: number,
}> (balances: T[]): number => {
  const today = businessToday();
  return balances
    .filter((balance) => (
      balance.bookingId === null
      && (!balance.expirationDate || balance.expirationDate >= today)
    ))
    .reduce((acc, balance) => acc + balance.points, 0);
};

/**
 * What can actually be spent right now: unexpired, unreserved and unlocked. Locked points are
 * excluded here rather than at each call site, so every surface agrees on what "available" means.
 */
export const calculateAvailablePoints = <T extends {
  bookingId: string | null,
  expirationDate: string | null,
  lockedUntilDate?: string | null,
  points: number,
}> (balances: T[]): number => {
  return calculateUnexpiredPoints(balances.filter((balance) => !isBalanceLocked(balance)));
};

export const calculateLockedPoints = <T extends {
  bookingId: string | null,
  expirationDate: string | null,
  lockedUntilDate?: string | null,
  points: number,
}> (balances: T[]): number => {
  return calculateUnexpiredPoints(balances.filter(isBalanceLocked));
};

export const calculateReservedPoints = <T extends {
  bookingId: string | null,
  expirationDate: string | null,
  points: number,
}> (balances: T[]): number => {
  return balances
    .filter((balance) => (
      balance.bookingId !== null
      && balance.expirationDate === null
    ))
    .reduce((acc, balance) => acc + balance.points, 0);
};

export const groupByDaysUntilExpiration = <T extends {
  expirationDate: string | null,
}> (wallets: T[], groups: number[] = [7, 30, 60, 90]): {
    [key: string]: (T & { expirationDate: string })[]
  } => {
  const nonExpiredWallets = wallets.filter(
    (wallet) => wallet.expirationDate && wallet.expirationDate >= businessToday(),
  );
  const walletGroups = groupBy(nonExpiredWallets,
    (wallet: T) => {
      const closestGroup = getClosestExpirationGroup(wallet.expirationDate, groups);
      return closestGroup === -1 ? "none" : closestGroup.toString();
    },
  );
  delete walletGroups["none"];
  return walletGroups as {
    [key: string]: (T & { expirationDate: string })[]
  };
};

export const groupByExpirationDate = (balances: { expirationDate: string | null }[]) => {
  const groups: { [expirationDate: string]: { expirationDate: string | null }[] } = {};
  for (const balance of balances) {
    const key = balance.expirationDate === null ? 'No expiration' : balance.expirationDate;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push({ ...balance, expirationDate: key });
  }
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    if (a === 'No expiration') return 1;
    if (b === 'No expiration') return -1;
    return a.localeCompare(b);
  });
  return sortedKeys.map(key => ({
    key,
    balances: groups[key],
  }));
};

export const getClosestExpirationGroup = (
  expirationDate: string | null,
  groups: number[] = [7, 30, 60, 90],
) => {
  for (const days of groups) {
    const daysFromNowDate = daysFromNow(days);
    if (expirationDate && expirationDate <= formatBusinessDate(daysFromNowDate)) {
      return days;
    }
  }
  return -1;
};
